import React, { useState } from "react";
import "./StudentPreviewModal.css";
import { baseurl } from '../../url';
import { getFeeCategoryDisplayName } from '../../utils';

const StudentPreviewModal = ({ student, onClose, onEdit }) => {
  const [imageError, setImageError] = useState(false);
  
  if (!student) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Function to get the correct image URL
  const getImageUrl = () => {
    // Try to get the photo path from different possible fields
    const photoPath = student.photo || student.photoUrl || student.profilePhoto;
    
    if (!photoPath || imageError) return null;
    
    // If it's already a full URL
    if (photoPath.startsWith('http')) {
      return photoPath;
    }
    
    // If it's a base64 string
    if (photoPath.startsWith('data:image')) {
      return photoPath;
    }
    
    // Construct the full URL by joining baseurl with the photo path
    // Remove leading slash if present to avoid double slashes
    const cleanPath = photoPath.startsWith('/') ? photoPath.substring(1) : photoPath;
    return `${baseurl}/${cleanPath}`;
  };

  const imageUrl = getImageUrl();

  // Helper to display nested object values
  const getNestedValue = (obj, path, defaultValue = "N/A") => {
    if (!obj) return defaultValue;
    const value = path.split('.').reduce((current, key) => current?.[key], obj);
    return value && value !== "" ? value : defaultValue;
  };

  return (
    <div className="preview-modal-overlay" onClick={onClose}>
      <div className="preview-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="preview-modal-header">
          <h2>Student Details Preview</h2>
          <button className="preview-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="preview-modal-body">
          {/* Student Image & Basic Info Row */}
          <div className="preview-student-header">
            <div className="preview-image-container">
              {imageUrl && !imageError ? (
                <img
                  src={imageUrl}
                  alt={student.name}
                  className="preview-student-image"
                  onError={(e) => {
                    console.error("Failed to load image from:", imageUrl);
                    setImageError(true);
                  }}
                  onLoad={() => console.log("Image loaded successfully:", imageUrl)}
                />
              ) : (
                <div className="preview-no-image">
                  <span>📷</span>
                  <p>No Image</p>
                </div>
              )}
            </div>
            <div className="preview-basic-info">
              <h1>{student.name || "N/A"}</h1>
              <div className="preview-basic-details">
                <span className="preview-badge">{student.course || "N/A"}</span>
                <span className="preview-badge preview-semester">
                  Semester {student.semester || "Not Assigned"}
                </span>
                <span className="preview-badge preview-admission">
                  📚 {student.admissionNumber || student.admissionId || "N/A"}
                </span>
                {student.academicYear && (
                  <span className="preview-badge preview-academic-year">
                    🗓️ {student.academicYear}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="preview-two-columns">
            {/* Personal Information */}
            <div className="preview-section">
              <h3 className="preview-section-title">
                <span className="section-icon">👤</span> Personal Information
              </h3>
              <div className="preview-info-grid">
                <div className="preview-info-item">
                  <label>Full Name</label>
                  <p>{student.name || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Date of Birth</label>
                  <p>{formatDate(student.dateOfBirth)}</p>
                </div>
                <div className="preview-info-item">
                  <label>Gender</label>
                  <p>{student.gender || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Blood Group</label>
                  <p>{student.bloodGroup || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Religion</label>
                  <p>{student.religion || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Community</label>
                  <p>{student.community || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Category</label>
                  <p>{student.category || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Nativity</label>
                  <p>{student.nativity || "N/A"}</p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="preview-section">
              <h3 className="preview-section-title">
                <span className="section-icon">📞</span> Contact Information
              </h3>
              <div className="preview-info-grid">
                <div className="preview-info-item">
                  <label>Mobile Number</label>
                  <p>{student.mobileNo || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>WhatsApp Number</label>
                  <p>{student.whatsappNo || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Email</label>
                  <p>{student.email || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Aadhaar Number</label>
                  <p>{student.aadharNo || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Pincode</label>
                  <p>{student.pincode || "N/A"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="preview-two-columns">
            <div className="preview-section">
              <h3 className="preview-section-title">
                <span className="section-icon">🏠</span> Current Address
              </h3>
              <p className="preview-address-text">{student.address || "N/A"}</p>
            </div>
            <div className="preview-section">
              <h3 className="preview-section-title">
                <span className="section-icon">🏡</span> Permanent Address
              </h3>
              <p className="preview-address-text">{student.permanentAddress || "N/A"}</p>
            </div>
          </div>

          {/* Academic Information */}
          <div className="preview-section">
            <h3 className="preview-section-title">
              <span className="section-icon">📚</span> Academic Information
            </h3>
            <div className="preview-info-grid three-columns">
              <div className="preview-info-item">
                <label>Admission Number</label>
                <p className="highlight-text">{student.admissionNumber || "N/A"}</p>
              </div>
              <div className="preview-info-item">
                <label>Academic Year</label>
                <p className="highlight-text">{student.academicYear || "N/A"}</p>
              </div>
              <div className="preview-info-item">
                <label>Course</label>
                <p>{student.course || "N/A"}</p>
              </div>
              <div className="preview-info-item">
                <label>Current Semester</label>
                <p>{student.semester || "N/A"}</p>
              </div>
              <div className="preview-info-item">
                <label>Admission Type</label>
                <p>{student.admissionType || "N/A"}</p>
              </div>
              <div className="preview-info-item">
                <label>Allotment Category</label>
                <p>{student.allotmentCategory || "N/A"}</p>
              </div>
              <div className="preview-info-item">
                <label>Fee Category</label>
                <p>{getFeeCategoryDisplayName(student.feeCategory) || "N/A"}</p>
              </div>
              <div className="preview-info-item">
                <label>Annual Income</label>
                <p>{student.annualIncome || "N/A"}</p>
              </div>
              <div className="preview-info-item">
                <label>Submission Date</label>
                <p>{formatDate(student.submissionDate)}</p>
              </div>
            </div>
          </div>

          {/* Entrance Exam Details */}
          <div className="preview-section">
            <h3 className="preview-section-title">
              <span className="section-icon">📝</span> Entrance Exam Details
            </h3>
            <div className="preview-info-grid three-columns">
              <div className="preview-info-item">
                <label>Exam Name</label>
                <p>{student.entranceExam || "N/A"}</p>
              </div>
              <div className="preview-info-item">
                <label>Register Number</label>
                <p>{student.entranceRollNo || "N/A"}</p>
              </div>
              <div className="preview-info-item">
                <label>Rank</label>
                <p>{student.entranceRank || "N/A"}</p>
              </div>
            </div>
          </div>

          {/* Plus Two Marks */}
          {student.marks && (
            <div className="preview-section">
              <h3 className="preview-section-title">
                <span className="section-icon">🎓</span> Plus Two Marks
              </h3>
              <div className="preview-info-grid three-columns">
                <div className="preview-info-item">
                  <label>Board</label>
                  <p>{student.marks.boardType || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Physics</label>
                  <p>{student.marks.physics || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Chemistry</label>
                  <p>{student.marks.chemistry || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Mathematics</label>
                  <p>{student.marks.maths || "N/A"}</p>
                </div>
              </div>
            </div>
          )}

          {/* Qualifying Exam Details */}
          {student.qualify && (
            <div className="preview-section">
              <h3 className="preview-section-title">
                <span className="section-icon">🎓</span> Qualifying Exam Details
              </h3>
              <div className="preview-info-grid three-columns">
                <div className="preview-info-item">
                  <label>Exam</label>
                  <p>{student.qualify.exam || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Board</label>
                  <p>{student.qualify.board || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Institution</label>
                  <p>{student.qualify.institution || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Register Number</label>
                  <p>{student.qualify.regNo || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Exam Year/Month</label>
                  <p>{student.qualify.examMonthYear || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Percentage</label>
                  <p>{student.qualify.percentage || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>CGPA</label>
                  <p>{student.qualify.cgpa || "N/A"}</p>
                </div>
              </div>
            </div>
          )}

          {/* Parent Details */}
          {student.parentDetails && (
            <div className="preview-section">
              <h3 className="preview-section-title">
                <span className="section-icon">👨‍👩‍👧</span> Parent/Guardian Details
              </h3>
              <div className="preview-two-columns">
                <div className="preview-info-grid">
                  <div className="preview-info-item">
                    <label>Father's Name</label>
                    <p>{student.parentDetails.fatherName || "N/A"}</p>
                  </div>
                  <div className="preview-info-item">
                    <label>Father's Mobile</label>
                    <p>{student.parentDetails.fatherMobileNo || "N/A"}</p>
                  </div>
                  <div className="preview-info-item">
                    <label>Father's Occupation</label>
                    <p>{student.parentDetails.fatherOccupation || "N/A"}</p>
                  </div>
                </div>
                <div className="preview-info-grid">
                  <div className="preview-info-item">
                    <label>Mother's Name</label>
                    <p>{student.parentDetails.motherName || "N/A"}</p>
                  </div>
                  <div className="preview-info-item">
                    <label>Mother's Mobile</label>
                    <p>{student.parentDetails.motherMobileNo || "N/A"}</p>
                  </div>
                  <div className="preview-info-item">
                    <label>Mother's Occupation</label>
                    <p>{student.parentDetails.motherOccupation || "N/A"}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Certificates Section */}
          {student.certificates && (
            <div className="preview-section">
              <h3 className="preview-section-title">
                <span className="section-icon">📜</span> Uploaded Certificates
              </h3>
              <div className="preview-info-grid three-columns">
                <div className="preview-info-item">
                  <label>Aadhaar Card</label>
                  <p className={student.certificates.aadhaar ? "status-yes" : "status-no"}>
                    {student.certificates.aadhaar ? "✓ Uploaded" : "✗ Not Uploaded"}
                  </p>
                </div>
                <div className="preview-info-item">
                  <label>Passport Size Photo</label>
                  <p className={student.certificates.passportsizephoto ? "status-yes" : "status-no"}>
                    {student.certificates.passportsizephoto ? "✓ Uploaded" : "✗ Not Uploaded"}
                  </p>
                </div>
                <div className="preview-info-item">
                  <label>Plus Two Certificate</label>
                  <p className={student.certificates.plusTwo ? "status-yes" : "status-no"}>
                    {student.certificates.plusTwo ? "✓ Uploaded" : "✗ Not Uploaded"}
                  </p>
                </div>
                <div className="preview-info-item">
                  <label>Tenth Certificate</label>
                  <p className={student.certificates.tenth ? "status-yes" : "status-no"}>
                    {student.certificates.tenth ? "✓ Uploaded" : "✗ Not Uploaded"}
                  </p>
                </div>
                <div className="preview-info-item">
                  <label>TC & Conduct</label>
                  <p className={student.certificates.tcandconduct ? "status-yes" : "status-no"}>
                    {student.certificates.tcandconduct ? "✓ Uploaded" : "✗ Not Uploaded"}
                  </p>
                </div>
                <div className="preview-info-item">
                  <label>Community Certificate</label>
                  <p className={student.certificates.communitycertificate ? "status-yes" : "status-no"}>
                    {student.certificates.communitycertificate ? "✓ Uploaded" : "✗ Not Uploaded"}
                  </p>
                </div>
                <div className="preview-info-item">
                  <label>Degree Certificates</label>
                  <p className={student.certificates.degreecertificates ? "status-yes" : "status-no"}>
                    {student.certificates.degreecertificates ? "✓ Uploaded" : "✗ Not Uploaded"}
                  </p>
                </div>
                <div className="preview-info-item">
                  <label>Mark List</label>
                  <p className={student.certificates.marklist ? "status-yes" : "status-no"}>
                    {student.certificates.marklist ? "✓ Uploaded" : "✗ Not Uploaded"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Achievements Section */}
          {(student.achievements?.sports || student.achievements?.arts || student.achievements?.other) && (
            <div className="preview-section">
              <h3 className="preview-section-title">
                <span className="section-icon">🏆</span> Achievements
              </h3>
              <div className="preview-info-grid three-columns">
                <div className="preview-info-item">
                  <label>Sports</label>
                  <p>{student.achievements?.sports || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Arts</label>
                  <p>{student.achievements?.arts || "N/A"}</p>
                </div>
                <div className="preview-info-item">
                  <label>Other Achievements</label>
                  <p>{student.achievements?.other || "N/A"}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="preview-modal-footer">
          <button className="preview-edit-btn" onClick={onEdit}>
            ✏️ Edit Student
          </button>
          <button className="preview-close-footer-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentPreviewModal;